import { useEffect } from "react";
import Grid from "./UPGrid";
import { useParams } from "react-router";
import { isAxiosError } from "axios";
import { uqActions } from "@redux/uq/uq-slice";
import { calculateAge } from "./up-utils";
import { CalculateGrid } from "@redux/uq/uq-actions";
import UniquePossibilitiesSkeleton from "./UniquePossibilitiesSkeleton";
import withDisclaimer from "@/hoc/withDisclaimer";
import { useUniquePossibilitiesUpsert } from "@/hooks/useUniquePossibilitiesUpsert";
import { toast } from "react-toastify";
import { UPWrapper } from "@/types/types";
import { NoContactBirthDateMessage } from "../HouseholdContact";
import { checkClientsBirthdates, devLog } from "@/helper/helper";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import useHeaderChange from "@/hooks/useHeaderChange";
import { useAppSelector } from "@/hooks/useAppSelector";
import { UNIQUE_POSSIBILITIES } from "@/constants/constants";

function UniquePossibilities() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const {
    mutateAsync: upsertUq,
    error: upsertError,
    isError: upsertIsError,
    isPending: upUpsertLoading,
  } = useUniquePossibilitiesUpsert();
  const { reload } = useAppSelector((state) => state.uq);

  const { household, upData, upError, upIsError, upLoaded, upLoading, upRefetching } =
    useHeaderChange(id as string, "Unique Possibilities Solution");

  useEffect(() => {
    if (upsertIsError) {
      toast.error(isAxiosError(upsertError) ? upsertError.message : JSON.stringify(upsertError));
    }
  }, [upsertError, upsertIsError]);

  useEffect(() => {
    if (!upLoaded || !upData) return;
    if (household?.isSingle && !household.primaryContact) return;

    const modifiedOn = upData?.modifiedOn || new Date();
    const parsedData: UPWrapper | null =
      typeof upData?.data === "string" ? JSON.parse(upData?.data) : upData?.data;

    if (!parsedData) {
      const p1Age =
        calculateAge(household?.primaryContact?.birthDate?.toString(), 0)?.value ||
        UNIQUE_POSSIBILITIES.MinAge;
      const p2Age = household?.secondaryContact
        ? calculateAge(household.secondaryContact.birthDate?.toString(), 0)?.value
        : 0;

      dispatch(
        uqActions.setSettings({
          inflation: 0.035,
          cola: 0.025,
          ror: 0.07,
          person1: {
            beginAt: Math.max(p1Age, UNIQUE_POSSIBILITIES.MinAge),
            amount: 0,
            showAge: true,
            showSS: true,
          },
          person2: {
            beginAt: Math.max(p2Age, UNIQUE_POSSIBILITIES.MinAge),
            amount: 0,
            showAge: true,
            showSS: true,
          },
          colaIncome: null,
          colaIncome2: null,
          colaSS1: null,
          colaSS2: null,
          showRor: false,
          showSS: false,
        })
      );
      dispatch(uqActions.setModifiedOn(new Date().toISOString()));
      dispatch(CalculateGrid(0, []));
      dispatch(uqActions.setClientAgesFromGridSnapshot({ age1: p1Age, age2: p2Age }));
      return;
    }

    parsedData.settings = {
      ...parsedData.settings,
      person1: {
        ...parsedData.settings.person1,
        beginAt: parsedData.settings.person1.beginAt || UNIQUE_POSSIBILITIES.MinAge,
        showSS: parsedData.settings.person1.showSS ?? true,
        showAge: parsedData.settings.person1.showAge ?? true,
      },
      person2: {
        ...parsedData.settings.person2,
        beginAt: parsedData.settings.person2?.beginAt || UNIQUE_POSSIBILITIES.MinAge,
        showSS: parsedData.settings.person2?.showSS ?? true,
        showAge: parsedData.settings.person2?.showAge ?? true,
        amount: parsedData.settings.person2?.amount ?? 0,
      },
      showSS: parsedData.settings.showSS ?? false,
      showRor: parsedData.settings.showRor ?? false,
    };

    dispatch(uqActions.setSettings(parsedData.settings));
    if (parsedData.titles) dispatch(uqActions.setTitles(parsedData.titles));
    dispatch(
      uqActions.setModifiedOn(modifiedOn instanceof Date ? modifiedOn.toISOString() : modifiedOn)
    );
    dispatch(
      uqActions.setClientAgesFromGridSnapshot({
        age1: parsedData.gridSet[0].Age.value,
        age2: parsedData.gridSet[0].Age2.value,
      })
    );

    dispatch(CalculateGrid(0, parsedData.gridSet)).then(async (newData) => {
      dispatch(uqActions.setIsModified(false));
      const isForceSaveNeeded = newData?.length !== parsedData?.gridSet?.length;

      if (upData.forceSave || isForceSaveNeeded) {
        devLog("Force saving recalculated grid to DB", newData);
        await upsertUq({
          data: {
            modifiedOn: new Date().toISOString(),
            household: { externalCode: id as string },
            forceSave: false,
            data: JSON.stringify({
              gridSet: newData,
              settings: parsedData.settings,
              titles: parsedData.titles,
            }),
          },
          overwrite: true,
        });
        dispatch(uqActions.setIsModified(false));
      }
    });
  }, [upLoaded, upData, household, dispatch, upsertUq, id]);

  if (upLoading || upRefetching || upUpsertLoading || reload)
    return <UniquePossibilitiesSkeleton />;
  if (upIsError)
    return <div>{isAxiosError(upError) ? upError.message : "Failed fetching household"}</div>;
  if (checkClientsBirthdates(household)) return <NoContactBirthDateMessage household={household} />;

  return (
    <div className="print-zoom-80 flex flex-col gap-12">
      {household ? <Grid initGrid={upData} /> : null}
    </div>
  );
}

export default withDisclaimer(UniquePossibilities);
